import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  Col,
  ColProps,
  Form,
  Input,
  Row,
  notification,
} from 'antd';
import { User } from '../../services/user-service/types';
import { Sprite } from '../sprite';
import StringUtils, { errorMessage as ErrorMessage } from '../../utils';
import DatePicker from '../DatePicker';
import { CountryDropdown } from '../commons/country-dropdown';
import { StatesDropdown } from '../commons/state-dropdown';
import { useUserStore } from '../../store/user-store';

/**
 * EditCustomerDetails2 props
 */
interface Props {
  user: User;
}

interface UserForm {
  key: keyof User;
  required?: boolean;
  label?: string;
  colProps?: ColProps;
  render?: () => ReactElement;
  type?: 'date';
}
/**
 * EditCustomerDetails2 Component
 */
const EditCustomerDetails2: FunctionComponent<Props> = ({ user }) => {
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateUser } = useUserStore();

  const formFields = useMemo<UserForm[]>(
    () => [
      { key: 'firstName' },
      { key: 'lastName' },
      { key: 'birthDate', type: 'date' },
      { key: 'phone' },
      { key: 'addr1', required: false },
      { key: 'addr2', required: false },
      { key: 'country', required: false },
      { key: 'city', required: false },
      { key: 'state', required: false },
      { key: 'zipcode', required: false },
    ],
    [],
  );

  const {
    handleSubmit,
    formState: { errors, dirtyFields },
    getValues,
    setValue,
    control,
    watch,
  } = useForm<Partial<User>>({ defaultValues: user });

  const country = watch('country');

  useEffect(() => {
    if (country) {
      if (dirtyFields.country) {
        setValue('state', '');
      } else {
        setValue('state', user.state);
      }
    }
  }, [dirtyFields.country, country, setValue, user.state]);

  const onSubmit = handleSubmit(async data => {
    setIsLoading(true);
    try {
      await updateUser(Number(id), {
        ...data,
      });
      navigate(`/customers/${id}`);
    } catch (err) {
      api.error({ message: ErrorMessage(err) });
    }
    setIsLoading(false);
  });

  const zeroPad = (num: number, places: number) =>
    String(num).padStart(places, '0');

  const renderField = useCallback(
    (fieldForm: UserForm) => (
      <Controller
        control={control}
        name={fieldForm.key}
        rules={{ required: fieldForm.required !== false }}
        render={({ field: fieldProps }) => {
          if (fieldForm.render) {
            return fieldForm.render();
          }
          if (fieldForm.type === 'date') {
            const { onChange, value } = fieldProps;
            return (
              <DatePicker
                value={value ? new Date(value.toString()) : undefined}
                onChange={(_, val) => {
                  const cValue = `${zeroPad(val.month || 0, 2)}/${zeroPad(
                    val.day || 0,
                    2,
                  )}/${zeroPad(val.year || 0, 4)}`;
                  onChange({ target: { value: cValue } });
                }}
              />
            );
          }
          if (fieldForm.key === 'country') {
            return (
              <CountryDropdown
                className="w-auto"
                containerClassName="mt-0"
                defaultValue={fieldProps?.value?.toString() || ''}
                onValueChange={fieldProps.onChange}
              />
            );
          }
          if (fieldForm.key === 'state') {
            return (
              <StatesDropdown
                className="w-auto"
                value={fieldProps?.value?.toString() || ''}
                country={getValues('country')}
                onValueChange={fieldProps.onChange}
              />
            );
          }
          return <Input {...fieldProps} value={fieldProps.value?.toString()} />;
        }}
      />
    ),
    [control, getValues],
  );

  const errorMessage = useCallback(
    (field: UserForm) => {
      const error: FieldError | undefined = errors[field.key];
      if (error?.message) return error?.message;
      if (error?.type === 'required') {
        return 'this field is required.';
      }
      return error?.type;
    },
    [errors],
  );

  return (
    <>
      {contextHolder}
      <Card
        title={
          <div className="py-5">
            <Row gutter={10}>
              <Col>
                <Avatar
                  icon={
                    <Sprite
                      name="icon-create"
                      className="text-blue fill-bue py-3 px-3"
                    />
                  }
                  className="bg-white border-slate-100"
                  size={50}
                  gap={1}
                />
              </Col>
              <Col>
                <div className="text-xl pt-2.5">Edit profile</div>
              </Col>
            </Row>
          </div>
        }
        extra={
          <>
            <Button
              disabled={isLoading}
              onClick={() => navigate(`/customers/${id}`)}>
              Cancel
            </Button>
            <Button
              loading={isLoading}
              type="primary"
              className="ml-3 bg-red-900 text-white border-red-900"
              onClick={onSubmit}>
              Update profile
            </Button>
          </>
        }>
        <Form layout="vertical">
          <Row gutter={20}>
            {formFields.map(field => (
              <Col span={8} {...(field.colProps || {})}>
                <div className="pb-1">
                  <Form.Item
                    label={field.label || StringUtils.prettify(field.key)}
                    name={field.key}
                    validateStatus={errors[field.key] ? 'error' : undefined}
                    help={errorMessage(field)}>
                    {renderField(field)}
                  </Form.Item>
                </div>
              </Col>
            ))}
          </Row>
        </Form>
      </Card>
    </>
  );
};

export default EditCustomerDetails2;
